import { useLayoutEffect, useRef } from 'react';
import { computeScrollLeftForDate } from '../utils/timeline-geometry';
import type { TimelineZoom } from '../types';

/** How close (in px) the viewport must get to either end before the range grows that way. */
const EDGE_THRESHOLD_PX = 600;

interface UseTimelineScrollParams {
  zoom: TimelineZoom;
  windowStart: Date;
  windowEnd: Date;
  totalWidthPx: number;
  /**
   * Whether the grid (not a skeleton/empty/error state) is what's actually
   * mounted inside the container. Centering or extending before that is
   * meaningless — the container has nothing wider than itself to scroll.
   */
  isGridMounted: boolean;
  /** Prepends more range. Returns the px added on the left, so the scroll position can be preserved. */
  onExtendPast: () => number;
  /** Appends more range to the right. */
  onExtendFuture: () => void;
}

/**
 * Owns the Timeline's horizontal-scroll container and turns plain native
 * scrolling into an endlessly-pannable range: nearing either end grows the
 * grid in that direction instead of dead-ending at a preloaded boundary.
 *
 * Scroll position itself is never mirrored into React state — only the loaded
 * range is — so panning stays at the browser's own frame rate.
 */
export function useTimelineScroll({
  zoom,
  windowStart,
  windowEnd,
  totalWidthPx,
  isGridMounted,
  onExtendPast,
  onExtendFuture,
}: UseTimelineScrollParams) {
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Width just prepended, still to be added back to `scrollLeft` (see the layout effect below). */
  const pendingLeftShiftPx = useRef(0);
  /** Blocks re-entrant growth while a pending extension is still rendering. */
  const isExtending = useRef(false);

  function scrollToToday(behavior: ScrollBehavior) {
    const el = scrollRef.current;
    if (!el) return;
    const left = computeScrollLeftForDate(
      new Date(),
      windowStart,
      windowEnd,
      totalWidthPx,
      el.clientWidth,
    );
    el.scrollTo({ left, behavior });
  }

  // Center on "today" once the grid is actually mounted (first load finishing,
  // or a zoom switch rebuilding the range). Waiting on `isGridMounted` matters:
  // while the skeleton is up the container is no wider than itself, so a
  // `scrollTo` would clamp to 0 and silently leave the view pinned to the left
  // edge — right on the extend-past threshold. Deliberately NOT keyed on
  // windowStart: growing the range also moves it, and that must preserve the
  // user's position.
  useLayoutEffect(() => {
    if (!isGridMounted) return;
    pendingLeftShiftPx.current = 0;
    isExtending.current = false;
    scrollToToday('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a zoom switch (or the grid first appearing) recenters
  }, [zoom, isGridMounted]);

  // Prepending pushes every existing column right by the added width, so the
  // content under the viewport would visibly jump backwards. Adding that same
  // width to `scrollLeft` before paint makes the growth seamless.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (pendingLeftShiftPx.current > 0) {
      el.scrollLeft += pendingLeftShiftPx.current;
      pendingLeftShiftPx.current = 0;
    }
    isExtending.current = false;
  }, [totalWidthPx]);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el || isExtending.current || !isGridMounted) return;
    // Nothing overflows yet (skeleton, or a grid narrower than the viewport) —
    // growing the range here would just fire requests the user can't scroll into.
    if (el.scrollWidth <= el.clientWidth) return;

    if (el.scrollLeft < EDGE_THRESHOLD_PX) {
      isExtending.current = true;
      pendingLeftShiftPx.current = onExtendPast();
      return;
    }

    const distanceToEnd = el.scrollWidth - el.scrollLeft - el.clientWidth;
    if (distanceToEnd < EDGE_THRESHOLD_PX) {
      isExtending.current = true;
      onExtendFuture();
    }
  }

  return { scrollRef, scrollToToday, handleScroll };
}
